import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

export default function Catalog() {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ brand: "", model: "", type: "" });

  const load = () => api.get("/car-catalog").then((r) => setEntries(r.data.entries || []));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return entries;
    return entries.filter((e) =>
      (e.brand || "").toLowerCase().includes(s) ||
      (e.model || "").toLowerCase().includes(s) ||
      (e.type || "").toLowerCase().includes(s)
    );
  }, [entries, q]);

  const openNew = () => { setEditing(null); setForm({ brand: "", model: "", type: "" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ brand: it.brand, model: it.model, type: it.type || "" }); setOpen(true); };

  const submit = async () => {
    try {
      if (!form.brand || !form.model) return toast.error("Merek dan model wajib diisi");
      if (editing) await api.put(`/car-catalog/${editing.id}`, form);
      else await api.post("/car-catalog", form);
      toast.success("Tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus entri katalog?")) return;
    await api.delete(`/car-catalog/${id}`); toast.success("Terhapus"); load();
  };

  return (
    <div>
      <PageHeader
        title="Katalog Mobil"
        subtitle="Kelola daftar merek, model, dan type mobil yang dipakai autocomplete form"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-catalog-btn" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                <Plus size={14} className="mr-2" /> Tambah Entri
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-[95vw] max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Tambah"} Katalog</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div><Label className="text-xs uppercase tracking-wider">Merek</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-2 rounded-none" data-testid="cat-brand" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Model</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-2 rounded-none" data-testid="cat-model" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Type / Varian</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-2 rounded-none" data-testid="cat-type" placeholder="Contoh: Veloz 1.5 AT" /></div>
                <Button onClick={submit} data-testid="cat-submit" className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">Simpan</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-4 md:p-6 lg:p-10">
        <SearchBar value={q} onChange={setQ} placeholder="Cari merek / model / type..." testid="catalog-search" count={filtered.length} />
        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-4">Merek</th>
                <th className="p-4">Model</th>
                <th className="p-4">Type / Varian</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`catalog-row-${it.id}`}>
                  <td className="p-4 font-semibold">{it.brand}</td>
                  <td className="p-4">{it.model}</td>
                  <td className="p-4 text-gray-600">{it.type || "-"}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => openEdit(it)} className="mr-3 text-gray-500 hover:text-black" data-testid={`catalog-edit-${it.id}`}><PencilSimple size={16} /></button>
                    <button onClick={() => del(it.id)} className="text-gray-500 hover:text-[#E63946]" data-testid={`catalog-del-${it.id}`}><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="4" className="p-10 text-center text-sm text-gray-500">{entries.length === 0 ? "Belum ada entri" : "Tidak ada hasil"}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
